import React from 'react';
import { Helmet } from 'react-helmet';
import '../../styles/theme.css';

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | OptiBill Analytics</title>
        <meta name="description" content="OptiBill Analytics Privacy Policy - How we collect, use, and protect your personal and health information in compliance with HIPAA regulations." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <div className="legal-shell">
        <div className="legal-container">
          <div className="legal-card">
            {/* Header */}
            <div className="legal-header">
              <h1 className="text-navy fw-bold" style={{ fontSize: 36, marginBottom: 12 }}>Privacy Policy</h1>
              <p style={{ color: '#5A6478', fontSize: 16 }}>Last Updated: March 21, 2026</p>
            </div>

            {/* Introduction */}
            <section className="legal-section">
              <p style={{ color: '#5A6478', fontSize: 17, lineHeight: 1.7, marginBottom: 16 }}>
                At OptiBill Analytics ("we," "us," or "our"), we are committed to protecting your privacy and maintaining the security of your personal and health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
              </p>
              <p style={{ color: '#5A6478', fontSize: 17, lineHeight: 1.7 }}>
                As a medical billing and revenue cycle management service provider, we are subject to the Health Insurance Portability and Accountability Act (HIPAA) and maintain strict compliance with all applicable privacy and security regulations.
              </p>
            </section>

            {/* Section 1 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>1. Information We Collect</h2>
              
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>1.1 Personal Information</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                We may collect personal information that you voluntarily provide to us, including:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li>Name and contact information (email address, phone number, mailing address)</li>
                <li>Business information (company name, job title, practice information)</li>
                <li>Account credentials (username, password)</li>
                <li>Payment and billing information</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>1.2 Protected Health Information (PHI)</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                In the course of providing medical billing and RCM services, we may process Protected Health Information on behalf of our healthcare provider clients, including:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li>Patient demographics and medical records</li>
                <li>Insurance and billing information</li>
                <li>Healthcare service and treatment information</li>
                <li>Claims and payment data</li>
              </ul>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                <strong>Important:</strong> When handling PHI, we act as a Business Associate under HIPAA and comply with all applicable regulations. We process PHI only as necessary to provide our services and in accordance with our Business Associate Agreements.
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>1.3 Automatically Collected Information</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                When you visit our website, we may automatically collect:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li>Device and browser information (IP address, browser type, operating system)</li>
                <li>Usage data (pages visited, time spent, referring URLs)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>2. How We Use Your Information</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                We use the information we collect for the following purposes:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li>To provide, maintain, and improve our medical billing and RCM services</li>
                <li>To process insurance claims and manage accounts receivable</li>
                <li>To communicate with you about our services, updates, and support</li>
                <li>To respond to your inquiries and consultation requests</li>
                <li>To comply with legal obligations and regulatory requirements</li>
                <li>To detect, prevent, and address technical issues or fraudulent activity</li>
                <li>To analyze and improve our website and service offerings</li>
                <li>To send you marketing communications (with your consent, where required)</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>3. Information Sharing and Disclosure</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                We do not sell, rent, or trade your personal information. We may share information in the following circumstances:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li><strong>Service Providers:</strong> With trusted third-party vendors who assist in operating our business (e.g., payment processors, cloud hosting providers), under strict confidentiality agreements</li>
                <li><strong>Healthcare Entities:</strong> With payers, clearinghouses, and other healthcare entities as necessary to process claims and manage billing</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with appropriate safeguards</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
              </ul>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                All third-party service providers are required to maintain appropriate security measures and comply with applicable privacy laws, including HIPAA where applicable.
              </p>
            </section>

            {/* Section 4 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>4. Data Security</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                We implement and maintain robust security measures to protect your information, including:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li>Encryption of data in transit and at rest</li>
                <li>Secure access controls and authentication mechanisms</li>
                <li>Regular security assessments and audits</li>
                <li>Employee training on data security and privacy practices</li>
                <li>Incident response and breach notification procedures</li>
                <li>HIPAA-compliant infrastructure and workflows</li>
              </ul>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                While we strive to protect your information using industry-standard security practices, no method of transmission or storage is 100% secure. We cannot guarantee absolute security but are committed to promptly addressing any security concerns.
              </p>
            </section>

            {/* Section 5 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>5. Your Rights and Choices</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                You have the following rights regarding your personal information:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
              </ul>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                For PHI processed on behalf of healthcare providers, please contact the provider directly regarding your HIPAA rights. For questions about your rights or to exercise them, contact us at info@optibillanalytics.com.
              </p>
            </section>

            {/* Section 6 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>6. Data Retention</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                We retain personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. PHI is retained in accordance with HIPAA requirements and our Business Associate Agreements, typically for a minimum of six years from the date of creation or last use.
              </p>
            </section>

            {/* Section 7 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>7. Children's Privacy</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we learn that we have collected information from a child without parental consent, we will promptly delete it.
              </p>
            </section>

            {/* Section 8 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>8. International Data Transfers</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                Our primary operations are based in India, and we serve healthcare providers globally, including in the United States. When we transfer data internationally, we implement appropriate safeguards to ensure compliance with applicable privacy laws, including HIPAA for U.S. healthcare data.
              </p>
            </section>

            {/* Section 9 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>9. Changes to This Privacy Policy</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of material changes by posting the updated policy on our website with a new "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Section 10 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>10. Contact Us</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="legal-callout">
                <p style={{ color: '#2B4278', fontSize: 16, marginBottom: 8 }}><strong>OptiBill Analytics</strong></p>
                <p style={{ color: '#5A6478', fontSize: 16, marginBottom: 4 }}>Email: info@optibillanalytics.com</p>
                <p style={{ color: '#5A6478', fontSize: 16, marginBottom: 4 }}>Privacy Officer: Jayesh Gadhvi</p>
                <p style={{ color: '#5A6478', fontSize: 16 }}>Website: www.optibillanalytics.com</p>
              </div>
            </section>

            {/* Footer Note */}
            <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid rgba(16,35,62,0.12)', textAlign: 'center' }}>
              <p style={{ color: '#5A6478', fontSize: 14, fontStyle: 'italic', marginBottom: 12 }}>
                This Privacy Policy is effective as of the date stated above and governs our collection and use of information from that date forward.
              </p>
              <p style={{ color: '#5A6478', fontSize: 13, fontStyle: 'italic', background: '#f7fafc', padding: '12px 16px', borderRadius: 8, border: '1px solid #e6eef7', display: 'inline-block', textAlign: 'center' }}>
                <strong>Legal Disclaimer:</strong> This document does not constitute legal advice. Consult your attorney for specific guidance regarding your situation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
